import { defineStore } from 'pinia'
import { ref, computed, watch, reactive } from 'vue'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useAccountStore } from '@/store'
import { http } from '@/utils'
import type {
  MarketState,
  MarketSession,
  MarketCalendarConfig,
  ExtendedMarketState,
  HolidayInfo,
} from '@/composables/marketCalendar/types'
import { getSymbolMarketStatus, buildHolidayMap, toHHMM } from '@/composables/marketCalendar/utils'
import { registerTask, removeTask, updateTask } from '@/services/timerService'

dayjs.extend(utc)
dayjs.extend(timezone)

const MARKET_CALENDAR_GLOBAL_TASK_KEY = 'market-calendar-global'

const useMarketCalendarStore = defineStore('marketCalendar', () => {
  const accountStore = useAccountStore()
  let registered = false
  let earliest: Dayjs | null = null // 当前“最早变动”的时间点
  let earliestSymbol = '' // 记录 earliest 对应的 symbol，方便删除时判断
  // 状态
  const symbolSessions = ref<Map<string, MarketSession[]>>(new Map())
  const marketStates = ref<Map<string, ExtendedMarketState>>(new Map())
  const holidays = ref<Set<string>>(new Set())
  const serverOffset = computed(() => accountStore.selectedAccount?.server_offset_seconds || 0)
  const symbolSessionMap: Record<string, Record<number, MarketSession[]>> = reactive({})
  // 只支持一个市场，holidayMap 结构为 Map
  const holidayMap = ref<{
    ALL: Map<string, HolidayInfo>
    [symbol: string]: Map<string, HolidayInfo>
  }>({ ALL: new Map() })
  const known = new Set<string>()
  const allSymbolMarketStatus: Record<string, MarketState> = reactive({})
  const symbolTimeZoneMap: Record<string, string> = reactive({})

  // 注册 Symbol 交易时段
  /**
   * 注入单个 symbol 的 sessions_trade
   * @param symbol        例：'AUDSGD.sd'
   * @param sessionsTrade 7×N 数组，open/close 为 "当天第 N 分钟"(0-1439)
   * @param serverOffset  服务器相对 UTC 偏移：3、-2、5.5…；也可直接传 IANA 时区名
   * @param marketId      市场 ID，用于区分不同市场（如 'MAIN', 'HK', 'US'）
   */
  function registerSessions(
    symbol: string,
    sessionsTrade: { open: number; close: number }[][],
    serverOffset: number | string = 3,
  ) {
    // 若传数字偏移 → 转成 IANA 名称；Etc/GMT 的正负方向与直觉相反
    const serverTz =
      typeof serverOffset === 'number'
        ? `Etc/GMT${serverOffset > 0 ? '-' : '+'}${Math.abs(serverOffset)}`
        : serverOffset

    /* 写入时区映射，供 useMarketState 直接读取，避免每次遍历 */
    symbolTimeZoneMap[symbol] = serverTz
    symbolSessionMap[symbol] ??= {}

    sessionsTrade.forEach((dayArr, dayOfWeek) => {
      if (!dayArr?.length) return

      // 若一天内有多段交易（存在午休等），合并为单条 session，并补 breaks；
      if (dayArr.length > 1) {
        const breaks = dayArr.slice(0, -1).map((seg, idx) => {
          const startMinute = seg.close + 1
          const endMinute = dayArr[idx + 1].open - 1
          return {
            start: toHHMM(startMinute),
            end: toHHMM(endMinute),
            startMinute,
            endMinute,
          }
        })

        const openMinute = dayArr[0].open
        const closeMinute = dayArr[dayArr.length - 1].close

        symbolSessionMap[symbol][dayOfWeek] = [
          {
            symbol: symbol,
            dayOfWeek: dayOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6,
            open: toHHMM(openMinute),
            close: toHHMM(closeMinute),
            openMinute,
            closeMinute,
            breaks,
            tz: serverTz,
          } as MarketSession,
        ]
      } else {
        const { open, close } = dayArr[0]
        symbolSessionMap[symbol][dayOfWeek] = [
          {
            symbol,
            dayOfWeek: dayOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6,
            open: toHHMM(open), // 服务器挂钟 HH:mm
            close: toHHMM(close), // 服务器挂钟 HH:mm
            openMinute: open,
            closeMinute: close,
            breaks: null,
            tz: serverTz, // 服务器时区
          } as MarketSession,
        ]
      }
    })
  }

  // 清空所有数据
  function clear() {
    removeTask(MARKET_CALENDAR_GLOBAL_TASK_KEY)
    symbolSessions.value.clear()
    marketStates.value.clear()
    holidays.value.clear()
    console.log('[MarketCalendar] 数据已清空')
  }

  // 异步获取节假日并设置 holidayMap
  async function fetchAndSetHolidays() {
    const { data } = await http.get('/api/market/holidays')
    holidayMap.value = buildHolidayMap(data)
  }

  // 判断是否为节假日（优先 symbol，再 ALL）
  function isHoliday(symbol: string, date: string) {
    return holidayMap.value[symbol]?.has(date) || holidayMap.value.ALL.has(date)
  }

  // 获取节假日详情（如需）
  function getHolidayInfo(symbol: string, date: string): HolidayInfo | undefined {
    return holidayMap.value[symbol]?.get(date) || holidayMap.value.ALL.get(date)
  }

  function ensureGlobalMarketCalendarTask(nextTime: Dayjs) {
    // 直接注册任务，保证回调一定存在且不会重复注册
    registerTask(MARKET_CALENDAR_GLOBAL_TASK_KEY, nextTime, () => recalcAll(dayjs()))
  }

  function recalcAll(now: Dayjs) {
    earliest = null
    earliestSymbol = ''
    known.clear()

    for (const symbol of Object.keys(symbolSessionMap)) {
      const state = getSymbolMarketStatus({
        symbol,
        now,
        symbolHolidayMap: holidayMap.value[symbol] ?? new Map(),
        symbolSessionMap,
        symbolTimeZoneMap,
      })
      allSymbolMarketStatus[symbol] = state
      known.add(symbol)

      if (!earliest || state.nextChange.isBefore(earliest)) {
        earliest = state.nextChange
        earliestSymbol = symbol
      }
    }
    if (earliest) ensureGlobalMarketCalendarTask(earliest)
  }

  watch(
    () => Object.keys(symbolSessionMap),
    (newKeys, oldKeys) => {
      if (newKeys.length === 0) return
      const now = dayjs() // 本轮共用
      let needRescan = false // 标记是否需要整表重扫
      /* —— 新增 —— */
      for (const symbol of newKeys) {
        if (known.has(symbol)) continue
        known.add(symbol)
        const symbolHolidayMap = holidayMap.value[symbol] ?? new Map()
        const symbolMarketStatus = getSymbolMarketStatus({
          symbol,
          now,
          symbolHolidayMap,
          symbolSessionMap,
          symbolTimeZoneMap,
        })
        allSymbolMarketStatus[symbol] = symbolMarketStatus

        // 若新增更早，则立即更新 earliest
        if (!earliest || symbolMarketStatus.nextChange.isBefore(earliest)) {
          earliest = symbolMarketStatus.nextChange
          earliestSymbol = symbol
        }
      }
      /* —— 删除 —— */
      for (const symbol of oldKeys) {
        if (newKeys.includes(symbol)) continue
        known.delete(symbol)
        delete allSymbolMarketStatus[symbol]

        // 若删掉的恰是 earliest，需要整表重扫
        if (symbol === earliestSymbol) needRescan = true
      }

      /* —— 更新调度 —— */
      if (needRescan) {
        // 重新遍历一次 allStates 以找新的 earliest（低频）
        earliest = null
        earliestSymbol = ''
        for (const [symbol, symbolMarketStatus] of Object.entries(allSymbolMarketStatus)) {
          if (!earliest || symbolMarketStatus.nextChange.isBefore(earliest)) {
            earliest = symbolMarketStatus.nextChange
            earliestSymbol = symbol
          }
        }
      }

      if (earliest) ensureGlobalMarketCalendarTask(earliest)
    },
    { deep: true },
  )

  // Idempotent initializer for holiday data
  let _initPromise: Promise<void> | null = null
  async function init() {
    if (_initPromise) return _initPromise
    _initPromise = (async () => {
      if (holidayMap.value.ALL && holidayMap.value.ALL.size > 0) return
      await fetchAndSetHolidays()
    })()
    return _initPromise
  }

  // 如果账号切换影响节假日，账号切换时也自动拉取
  watch(
    () => accountStore.selectedAccount,
    (account) => {
      if (account) {
        // fetchAndSetHolidays()
      }
    },
    { immediate: false },
  )

  return {
    symbolSessions: computed(() => symbolSessions.value),
    marketStates: computed(() => marketStates.value),
    holidays: computed(() => holidays.value),
    allSymbolMarketStatus,
    registerSessions,
    clear,
    fetchAndSetHolidays,
    init,
    isHoliday,
    getHolidayInfo,
  }
})

export { useMarketCalendarStore }
export default useMarketCalendarStore
